<template>
    <div class="flex flex-col gap-4">
        <label for="profile-name" class="flex flex-col gap-2">
            <span class="body-small-semibold uppercase text-control-label">{{ t("components.profile.edit.name") }}</span>
            <input
                id="profile-name"
                v-model="profileForm.name"
                type="text"
                autocomplete="name"
                :maxlength="NAME_MAX_LENGTH"
                class="w-full rounded-xl border border-control-border bg-control-bg px-3 py-2 body text-control-text-strong focus-visible:border-control-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-control-border-strong"
                :placeholder="hrisName ?? t('components.profile.edit.namePlaceholder')" />
        </label>
        <label for="profile-title" class="flex flex-col gap-2">
            <span class="body-small-semibold uppercase text-control-label">{{ t("components.profile.edit.jobTitle") }}</span>
            <input
                id="profile-title"
                v-model="profileForm.title"
                type="text"
                autocomplete="organization-title"
                class="w-full rounded-xl border border-control-border bg-control-bg px-3 py-2 body text-control-text-strong focus-visible:border-control-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-control-border-strong"
                :placeholder="hrisData?.job_title ?? t('components.profile.edit.jobTitlePlaceholder')" />
        </label>
        <label for="profile-division" class="flex flex-col gap-2">
            <span class="body-small-semibold uppercase text-control-label">{{ t("components.profile.edit.team") }}</span>
            <input
                id="profile-division"
                v-model="profileForm.division"
                type="text"
                class="w-full rounded-xl border border-control-border bg-control-bg px-3 py-2 body text-control-text-strong focus-visible:border-control-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-control-border-strong"
                :placeholder="hrisData?.department ?? t('components.profile.edit.teamPlaceholder')" />
        </label>
        <AccountSettingToggle id="profile-is-manager" v-model="profileForm.isManager" :label="t('components.profile.edit.leadTeam')" :bold="false" :compact="true" />
        <div class="flex items-center justify-end gap-3">
            <span v-if="saveError" role="alert" class="body-small text-button-danger">{{ saveError }}</span>
            <BaseButton theme="tertiary" size="sm" :disabled="isSaving" @click="emit('cancelled')"> {{ t("common.cancel") }} </BaseButton>
            <BaseButton theme="secondary" size="sm" :disabled="isSaving" @click="handleSave">
                {{ isSaving ? t("components.profile.edit.saving") : t("common.save") }}
            </BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { HrisData, UserBasic } from "~vue/appTypes";
import { type ExistingPublicProfile, type ProfilePayload } from "~vue/types/profile";
import AccountSettingToggle from "~vue/UserProfile/AccountSettingToggle.vue";
import { ACCESS } from "~vue/utils/constants";
import { computed, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";

import BaseButton from "./BaseButton.vue";

const { t } = useI18n();

// first_name and last_name are each varchar(150) in the DB; the full name is
// split on the first space, so cap the input at 150 + 1 space + 150 (BUG-862).
const NAME_MAX_LENGTH = 301;

const props = withDefaults(
    defineProps<{
        existingPublicProfile?: ExistingPublicProfile | null;
        userBasic?: UserBasic;
        basicInfo?: { full_name: string } | null;
        hrisData?: HrisData | null;
        defaultProfileAccess?: string;
    }>(),
    {
        existingPublicProfile: null,
        userBasic: () => ({}) as UserBasic,
        basicInfo: null,
        hrisData: null,
        defaultProfileAccess: ACCESS.ORGANIZATION,
    },
);

const hrisName = computed(() => {
    const parts = [props.hrisData?.first_name, props.hrisData?.last_name].filter(Boolean);
    return parts.length ? parts.join(" ") : null;
});

const emit = defineEmits<{
    saved: [];
    cancelled: [];
}>();

interface ProfileForm {
    name: string;
    title: string;
    division: string;
    isManager: boolean;
}

const profileForm = reactive<ProfileForm>({
    name: props.existingPublicProfile?.name ?? props.basicInfo?.full_name ?? props.userBasic?.full_name ?? "",
    title: props.existingPublicProfile?.title ?? props.userBasic?.job_title ?? "",
    division: props.existingPublicProfile?.division ?? props.userBasic?.division ?? "",
    isManager: props.existingPublicProfile?.is_manager ?? props.userBasic?.is_manager ?? false,
});

const saveError = ref<string | null>(null);
const isSaving = ref(false);

const savePayload = ref<ProfilePayload | null>(null);
const { execute: executeSave, error: saveApiError } = useFetch("/api/accounts/profile/", { immediate: false }).post(savePayload).json();

async function handleSave(): Promise<void> {
    isSaving.value = true;
    saveError.value = null;
    savePayload.value = {
        name: profileForm.name,
        title: profileForm.title,
        division: profileForm.division,
        is_manager: profileForm.isManager,
        access: props.existingPublicProfile?.access ?? props.defaultProfileAccess,
    };

    await executeSave();
    isSaving.value = false;

    if (saveApiError.value) {
        saveError.value = t("components.profile.edit.saveFailed");
    } else {
        emit("saved");
    }
}
</script>
